/**
 * IXD에서 자리를 찾지 못한 항목 목록. React·DOM 무관 순수 로직.
 *
 * 확인해야 할 것 중 **셀 위에 표시할 수 없는 것만** 여기 모인다. 충돌·자리
 * 겹침은 identity가 있어 해당 셀에서 바로 보여주지만, failures는 서버가 대응
 * IXD 셀을 찾지 못해 실패한 것이라 붙일 셀 자체가 없다.
 *
 * 그래서 이 목록은 "어느 셀에서도 볼 수 없는 문제"라는 분명한 정체를 갖는다.
 */

import * as Sentry from "@sentry/react";
import { makeCellTagKey } from "@/utils/workbook/cell-tag-key";
import type { CellNoticeTone } from "@/components/ixd-table";
import type {
  IxdValueSyncFailureReason,
  IxdValueSyncMember,
  IxdValueSyncMemberUnresolvedReason,
  IxdValueSyncPlanStatus,
  IxdValueSyncPlanTable,
  IxdValueSyncTagLike,
} from "@/apis/ixd-value-sync/types";

/** 축과 그 축에 붙은 멤버 한 쌍 */
export interface PlanDiagnosticMember {
  axis: string;
  member: string;
  /** null이면 정상 해석된 멤버 — 조합을 이해할 맥락으로만 온다 */
  unresolvedReason: IxdValueSyncMemberUnresolvedReason | null;
}

/** 확인 목록 한 줄. 전부 IXD에 자리가 없어 반영되지 못한 항목이다. */
export interface PlanDiagnosticRow {
  /** 같은 셀이 여러 줄로 갈리지 않도록 워크북 셀 단위로 만든 키 */
  key: string;
  kind: "not-applied";
  /** 계정 라벨. 없으면 태그 id로 대체한다 */
  label: string;
  /** 워크북 셀 주소. 사용자가 손댈 수 있는 최소 단위가 셀이다 */
  sourceAddress: string | null;
  /**
   * 축·멤버 쌍. 문자열로 미리 이어붙이지 않는다 — 쉼표로 이은 목록은 어디까지가
   * 한 쌍인지 읽히지 않아서, 화면이 쌍 단위로 묶어 그릴 수 있어야 한다.
   */
  members: PlanDiagnosticMember[];
  value: string;
  reason: IxdValueSyncFailureReason;
  /** 상세 사유를 만드는 데 쓰는 서버 진단 값들 */
  tableTag?: IxdValueSyncTagLike | null;
  /** 이 실패가 속한 그룹의 IXD 표 키. 3개월 거부 시 거부된 표를 가리킨다 */
  ixdTableKey?: string | null;
  periodType?: string | null;
  accumulatedType?: string | null;
  /**
   * 워크북 셀 태그 키를 만드는 데 쓰는 원본 id들.
   *
   * 라벨(label·members)은 사람에게 보여주는 값이라 같은 이름이 여러 셀에
   * 걸린다. 셀을 짚을 때는 반드시 이쪽 id를 쓴다.
   */
  lineItemId: string;
  begend?: string | null;
  memberIds: string[];
}

export interface PlanTableDiagnostics {
  /**
   * 서버가 이 표를 어떻게 판정했는지.
   *
   * "채우지 못한 셀"을 경고할지 가르는 기준이다 — failed가 아니면 변환에
   * 실패한 것이 없다는 뜻이고, 값이 안 들어간 셀은 애초에 워크북에 값이 없던
   * 셀이다. 그걸 경고로 띄우면 정상인 표마다 경고가 남는다.
   *
   * 표 계획이 없으면 success로 본다 — 실패한 것이 없으므로.
   */
  status: IxdValueSyncPlanStatus;
  /** IXD에 자리가 없어 반영되지 못한 셀 수 */
  notAppliedCount: number;
  /** IXD에서 표 자체를 못 찾았다. failures가 전부 table_not_found다 */
  isTableMissing: boolean;
  rows: PlanDiagnosticRow[];
}

export const EMPTY_DIAGNOSTICS: PlanTableDiagnostics = {
  status: "success",
  notAppliedCount: 0,
  isTableMissing: false,
  rows: [],
};

const UNRESOLVED_LABEL: Record<IxdValueSyncMemberUnresolvedReason, string> = {
  member_not_found: " 멤버가 IXD 표에 없습니다",
  axis_not_found: " 멤버의 축을 정의 트리에서 유도하지 못했습니다",
};

const REASON_LABEL: Record<IxdValueSyncFailureReason, string> = {
  table_not_found: "IXD에서 이 표를 찾지 못했습니다",
  line_item_not_found: "IXD 표에 대응하는 계정이 없습니다",
  member_mismatch: "축·멤버를 찾지 못했습니다",
  duplicate_axis: "한 축에 멤버가 둘 이상 붙어 있습니다",
  entry_conversion_failed: "해당 기간의 컨텍스트가 서식에 없습니다",
  quarter_acc_not_supported: "대응하는 표가 3개월 칸을 쓰지 않습니다",
  not_used_sum_axis: "합계열 미사용",
  uncovered_axis: "합계열 사용이 체크되지 않았습니다",
  value_rejected: "값 형식이 IXD 계정과 맞지 않습니다",
  currency_mismatch: "값의 통화가 IXD 계정의 통화와 다릅니다",
};

/**
 * 셀 하단에 상주하는 짧은 사유이자, 반영불가 패널의 사유 묶음 제목.
 *
 * 사유 문장(REASON_LABEL)과 달리 한 눈에 읽히는 길이여야 한다 — 셀은 좁고,
 * 긴 문장은 어차피 눌러서 보는 패널이 맡는다. 셀과 패널이 같은 말을 써야
 * 사용자가 "셀의 그 사유"와 "패널의 그 묶음"을 같은 것으로 읽는다.
 *
 * 표식만 있던 때는 셀을 눌러보기 전엔 무엇이 왜 막혔는지 알 수 없었다.
 */
const SHORT_REASON_LABEL: Record<IxdValueSyncFailureReason, string> = {
  table_not_found: "IXD에 표 없음",
  line_item_not_found: "IXD 표에 같은 계정 없음",
  member_mismatch: "IXD 표에 같은 축·멤버 없음",
  duplicate_axis: "한 축에 멤버 중복",
  entry_conversion_failed: "IXD에 해당 기간 없음",
  quarter_acc_not_supported: "IXD 표가 3개월 미지원",
  not_used_sum_axis: "IXD 표가 합계열 미사용",
  uncovered_axis: "합계열 사용 미체크",
  value_rejected: "IXD 계정과 값 형식 불일치",
  currency_mismatch: "IXD 계정과 통화 불일치",
};

/**
 * 반영불가 패널의 탭 갈래. 시안(Figma 9001-536622)의 탭은 둘뿐이다 —
 * `대응 계정 없음`과 `합계열 사용 미체크`.
 *
 * 사유 10종을 그대로 탭으로 펴면 320px 패널에 탭 줄이 넘치고, 한두 건짜리
 * 탭이 늘어나 "어디부터 손보나"가 오히려 흐려진다. 합계열 미체크만 가르는
 * 이유는 조치가 다르기 때문이다 — 그쪽은 IXD 표 설정 한 번으로 끝나고,
 * 나머지는 계정·축·기간을 건건이 맞춰야 한다.
 *
 * 항목별 진짜 사유는 각 줄의 붉은 상세 문구(`describeFailure`)와 셀 하단
 * 짧은 사유(`SHORT_REASON_LABEL`)가 그대로 들고 있다 — 탭만 접는다.
 */
type TabBucket = "line_item_not_found" | "uncovered_axis";

const tabBucketOf = (reason: IxdValueSyncFailureReason): TabBucket =>
  reason === "uncovered_axis" ? "uncovered_axis" : "line_item_not_found";

/**
 * 탭 줄에 늘 서는 갈래와 그 순서(시안 9164-588039 / 9164-588092).
 *
 * 한쪽 갈래에 건이 없어도 탭은 `(0)`으로 남고 누를 수만 없다 — 탭이 통째로
 * 사라지면 "이 표엔 합계열 문제가 없다"와 "그런 갈래가 있는 줄 몰랐다"가
 * 화면에서 구분되지 않는다. 순서도 건수와 무관하게 고정이다.
 */
export const UNAPPLIED_TAB_LABEL: Record<TabBucket, string> = {
  line_item_not_found: "대응 계정 없음",
  uncovered_axis: "합계열 사용 미체크",
};

/**
 * 탭이 서는 순서. 건수와 무관하게 고정이라 같은 자리에 늘 같은 갈래가 있다.
 *
 * 라벨 객체의 키 순서에 기대지 않고 따로 적는다 — 그쪽은 누가 정렬하거나
 * 갈래를 끼워 넣어도 이상할 게 없는 자리라, 화면의 탭 순서가 거기 묶여 있으면
 * 의도치 않게 바뀐다.
 */
export const UNAPPLIED_TAB_ORDER: readonly TabBucket[] = [
  "line_item_not_found",
  "uncovered_axis",
];

/**
 * 탭 아래 한 줄 안내. 시안(9001-536633)이 탭마다 한 문장을 고정해 둔다.
 *
 * 사유별로 갈아끼우지 않는다 — `대응 계정 없음` 갈래에는 사유가 여럿 섞여
 * 들어오고, 그중 한 사유의 문장을 쓰면 나머지 항목에 대해 거짓말이 된다.
 * 항목별 진짜 사유는 각 줄의 붉은 상세(`describeFailure`)가 그대로 말한다.
 */
const TAB_HINT: Record<TabBucket, string> = {
  line_item_not_found: "DSD에는 있지만 IXD 표에서 대응 계정을 찾지 못했습니다.",
  uncovered_axis: "IXD 표에서 합계열 사용이 체크되지 않았습니다.",
};

/**
 * 사유의 색 계열. 3개월 거부는 표를 못 찾은 것과 조치가 달라 색부터 가른다.
 *
 * 표식·셀 하단 문구·셀 바탕·패널이 모두 이 한 판정을 본다 — 각자 정하면 같은
 * 사유가 화면마다 다른 색으로 나온다.
 */
export const failureTone = (
  reason: IxdValueSyncFailureReason,
): CellNoticeTone =>
  reason === "quarter_acc_not_supported" ? "unsupported" : "blocked";

/** 모르는 사유는 "반영불가"로 물러난다 — 칩이 조용히 사라지면 안 된다. */
export const shortFailureLabel = (row: PlanDiagnosticRow): string =>
  SHORT_REASON_LABEL[row.reason] ?? "반영불가";

/**
 * 기간을 뺀 셀 자리 키. 같은 계정·멤버 조합이면 당기·전기가 한 자리다.
 *
 * 셀에 표식을 붙일 때 쓰는 키(`makeCellTagKey`)와 같은 재료를 쓴다 — 세는
 * 기준과 표시하는 기준이 다르면 "배지 36건, 화면 표식 18개"가 된다.
 */
const unappliedCellKey = (row: PlanDiagnosticRow): string =>
  makeCellTagKey({
    lineItemId: row.lineItemId,
    begend: row.begend,
    memberIds: row.memberIds,
    accumulatedType: row.accumulatedType,
  });

/** 반영불가 패널의 항목 한 줄. 셀로 데려가는 데 필요한 것만 들고 있다. */
export interface UnappliedItem {
  /** 셀의 `data-notice-key`와 같은 값 — 이걸로 그 셀을 찾아간다 */
  key: string;
  /**
   * 당기/전기. 같은 계정이 두 기간 모두 실패하는 일이 흔해서(IXD 표에 그
   * 계정이 아예 없으면 당기·전기가 같이 막힌다) 기간을 안 적으면 목록에 똑같은
   * 줄이 두 번 있는 것처럼 보인다 — "반영불가 36건인데 셀은 18개"의 정체다.
   *
   * 지금 패널은 시안(9001-536622 / 9001-536710)대로 기간을 적지 않는다.
   * 당기·전기를 한 줄로 합치는 기준으로는 그대로 쓰인다.
   */
  periodLabel: string;
  /** 계정 · 멤버. 사용자가 표에서 눈으로 찾는 이름 그대로 */
  label: string;
  value: string;
  /** 이 항목만의 상세 사유 문장 */
  detail: string;
}

/** 같은 사유끼리 묶은 한 덩어리. 사용자는 사유 단위로 조치한다 */
export interface UnappliedGroup {
  /** 사유 그 자체가 아니라 탭 갈래다 — 여러 사유가 한 갈래에 들어온다 */
  reason: TabBucket;
  /** 셀 표식과 같은 색 계열 */
  tone: CellNoticeTone;
  hint: string;
  items: UnappliedItem[];
}

/**
 * 반영불가 배지를 눌렀을 때 펼칠 목록.
 *
 * 셀 표식·셀 하단 사유와 같은 재료(diagnostics.rows)에서 만든다 — 패널이 따로
 * 세면 "배지 N건, 패널 M건"처럼 숫자가 어긋난다.
 *
 * 짚을 IXD 표가 없는 행은 뺀다. 셀에 사유를 붙이지 않는 행과 같은 기준이라,
 * 패널에서 눌렀는데 갈 셀이 없는 항목이 생기지 않는다.
 */
export const groupUnappliedValues = (
  diagnostics: PlanTableDiagnostics,
  tableName?: string,
  /** 같은 화면에 여러 표가 있어 행 키가 겹칠 수 있다 — 셀과 같은 접두사를 쓴다 */
  keyPrefix = "",
): UnappliedGroup[] => {
  const byBucket = new Map<TabBucket, UnappliedItem[]>();
  /** 사유+셀자리 → 주소로 특정된 항목. 주소 없는 행이 흡수될 자리다 */
  const addressedByCell = new Map<string, UnappliedItem>();

  const periodOf = (row: PlanDiagnosticRow) =>
    row.periodType ? (PERIOD_LABEL[row.periodType] ?? row.periodType) : "";

  const push = (row: PlanDiagnosticRow): UnappliedItem => {
    const item: UnappliedItem = {
      key: `${keyPrefix}${row.key}`,
      periodLabel: periodOf(row),
      label: [row.label, ...row.members.map(({ member }) => member)]
        .filter(Boolean)
        .join(" \u00B7 "),
      value: row.value,
      /*
       * 합계열 미체크는 상세가 사유 안내를 되풀이할 뿐이다 — 시안(9001-536710)
       * 에서도 항목에 적지 않는다. 빈 문자열이면 패널이 그 줄을 띄우지 않는다.
       */
      detail:
        row.reason === "uncovered_axis" ? "" : describeFailure(row, tableName),
    };
    const bucket = tabBucketOf(row.reason);
    const items = byBucket.get(bucket) ?? [];
    items.push(item);
    byBucket.set(bucket, items);
    return item;
  };

  const rows = diagnostics.rows.filter((row) => row.ixdTableKey);
  // 주소가 있는 행이 먼저다 — 주소 없는 행은 이들에게 흡수될 수 있어야 한다.
  for (const row of rows.filter((row) => row.sourceAddress)) {
    addressedByCell.set(`${row.reason}@${unappliedCellKey(row)}`, push(row));
  }
  /*
   * 주소 없는 행(전기 쪽에서 흔하다)은 같은 자리의 주소 있는 행에 합친다.
   *
   * IXD 표에 계정이 없으면 당기·전기가 같이 막혀 실패는 두 건으로 오지만,
   * 사용자가 손볼 자리는 한 곳이다. 두 건으로 세면 배지 숫자가 화면의 표식
   * 개수와 어긋난다(실측: 36건 vs 표식 18개). 주소가 서로 다른 행끼리는
   * 합치지 않는다 — 그건 진짜 다른 셀이다.
   */
  for (const row of rows.filter((row) => !row.sourceAddress)) {
    const merged = addressedByCell.get(
      `${row.reason}@${unappliedCellKey(row)}`,
    );
    if (!merged) {
      push(row);
      continue;
    }
    const period = periodOf(row);
    if (period && !merged.periodLabel.includes(period)) {
      merged.periodLabel = [merged.periodLabel, period]
        .filter(Boolean)
        .join(" \u00B7 ");
    }
  }

  return (
    [...byBucket.entries()]
      // 많은 갈래부터 — 먼저 손볼 것이 위에 온다(툴팁 요약과 같은 순서).
      .sort(([, a], [, b]) => b.length - a.length)
      .map(([bucket, items]) => ({
        reason: bucket,
        tone: failureTone(bucket),
        hint: TAB_HINT[bucket],
        items,
      }))
  );
};

/**
 * 반영 못 한 **셀 자리** 수. 배지와 셀 표식이 같은 수를 말하게 하는 기준이다.
 *
 * `PlanTableDiagnostics.notAppliedCount`는 값(셀 × 기간) 건수라 같은 계정이
 * 당기·전기로 두 번 세어진다. 반영 결과 토스트처럼 "몇 건이 안 들어갔나"를
 * 말하는 자리는 그쪽을, 표 제목 옆 배지처럼 "어디를 손봐야 하나"를 말하는
 * 자리는 이쪽을 쓴다.
 */
export const countUnappliedCells = (
  groups: readonly UnappliedGroup[],
): number => groups.reduce((total, group) => total + group.items.length, 0);

/** 서버 어휘를 사람이 읽는 말로. 모르는 값은 원문을 그대로 둔다. */
const PERIOD_LABEL: Record<string, string> = {
  CURRENT: "당기",
  PRIOR: "전기",
  PREVIOUS: "전기",
};
const ACCUMULATED_LABEL: Record<string, string> = {
  ACC: "누적",
  QUARTER_ACC: "분기 누적",
};

const quote = (text: string) => `\u2018${text}\u2019`;

/**
 * "무엇을" 못 찾았는지까지 말한다.
 *
 * 사유만 알려주면 사용자는 표 전체를 훑어야 한다. 실패한 계정·멤버·기간이
 * 응답에 이미 실려 오므로 그대로 문장에 넣는다 — 고칠 자리를 바로 짚어준다.
 * 값이 없으면 일반 문구로 물러난다.
 */
/**
 * 문장에 넣을 표 이름.
 *
 * 태그 키(entity_footable)는 사람이 읽는 이름이 아니다. 태그 객체가 한글
 * 라벨을 들고 오면 그것을, 아니면 화면이 아는 표 이름을 쓰고, 둘 다 없을 때만
 * 키로 물러난다.
 */
const describeTable = (
  row: PlanDiagnosticRow,
  tableName: string | undefined,
): string => {
  if (row.tableTag && typeof row.tableTag !== "string") {
    const label = row.tableTag.labelKo ?? "";
    if (label) return label;
  }
  if (tableName) return tableName;
  const tag = row.tableTag ? describeTag(row.tableTag) : "";
  return tag || row.ixdTableKey || "";
};

/**
 * 버튼 툴팁용 요약 — 사유별로 묶어 "몇 건이 왜 안 들어가는지"만 말한다.
 *
 * 행마다 한 줄씩 쓰면 같은 사유가 수십 줄로 반복된다. 어느 셀인지는 셀 표식이
 * 짚고 있으므로 여기서는 사유와 건수까지만 말한다.
 */
export const summarizeFailures = (
  diagnostics: PlanTableDiagnostics,
): string[] => {
  const countByReason = new Map<IxdValueSyncFailureReason, number>();
  for (const row of diagnostics.rows) {
    countByReason.set(row.reason, (countByReason.get(row.reason) ?? 0) + 1);
  }
  return (
    [...countByReason.entries()]
      // 많은 사유부터 — 먼저 손볼 것이 위에 온다.
      .sort(([, a], [, b]) => b - a)
      .map(
        ([reason, count]) =>
          `${count}건은 입력하지 않습니다 \u2014 ${REASON_LABEL[reason] ?? reason}`,
      )
  );
};

export const describeFailure = (
  row: PlanDiagnosticRow,
  /** 화면이 아는 이 표의 한글 이름 */
  tableName?: string,
): string => {
  // 맥락으로 붙는 목록이라 항목마다 따옴표를 두르지 않는다.
  const members = row.members.map(describeMember).join(", ");
  /*
   * 프론트가 모르는 사유는 문구가 undefined가 되어, 표식은 붙는데 눌러도
   * 아무 말이 없는 셀이 된다 — 사유가 늘 때마다 조용히 사라지는 자리다.
   *
   * 여러 실패를 한 번에 다루는 자리라 통째로 던지지 않는다. 그 항목만
   * 원문으로 내려앉히고 Sentry로 보고해 다음 사유를 놓치지 않는다.
   */
  if (!(row.reason in REASON_LABEL)) {
    Sentry.captureMessage("모르는 값 반영 실패 사유", {
      level: "warning",
      extra: { reason: row.reason },
    });
    return `반영하지 못했습니다 (사유: ${row.reason})`;
  }
  switch (row.reason) {
    case "line_item_not_found":
      return `IXD 표에 ${quote(row.label)} 계정이 없습니다`;
    case "member_mismatch": {
      // 못 찾은 멤버만 지목한다. 목록 전체를 실패로 보여주면 어느 태그를
      // 고쳐야 할지 알 수 없다.
      //
      // 정상 멤버는 붙이지 않는다. 실제 라벨이 "금융자산의 종류 [축] ›
      // 파생상품 [구성요소]" 처럼 길어서, 조합을 덧붙이면 정작 원인이 묻힌다.
      const unresolved = row.members.filter(
        ({ unresolvedReason }) => unresolvedReason !== null,
      );
      if (unresolved.length === 0) {
        return members
          ? `축·멤버 중 일부를 IXD에서 찾지 못했습니다 (${members})`
          : REASON_LABEL.member_mismatch;
      }

      const names = unresolved.map(describeMember).map(quote).join(", ");
      const reasons = new Set(
        unresolved.map(({ unresolvedReason }) => unresolvedReason),
      );
      const detail =
        reasons.size === 1
          ? UNRESOLVED_LABEL[
              [...reasons][0] as IxdValueSyncMemberUnresolvedReason
            ]
          : "을(를) IXD에서 해석하지 못했습니다";
      return `${names}${detail}`;
    }
    case "duplicate_axis":
      return members
        ? `한 축에 멤버가 둘 이상 붙어 있습니다 \u2014 ${members}`
        : REASON_LABEL.duplicate_axis;
    case "table_not_found": {
      const label = describeTable(row, tableName);
      return label
        ? `IXD에서 ${quote(label)} 표를 찾지 못했습니다`
        : REASON_LABEL.table_not_found;
    }
    // "찾지 못했습니다"가 아니라 "쓰지 않습니다"라고 말하는 것 자체가 표를 못
    // 찾은 것과 구분해 준다. 조치 안내까지 붙이면 표 이름이 길어 문장이 읽히지
    // 않으므로, 구분은 문구와 표식 색이 맡는다.
    case "quarter_acc_not_supported": {
      const label = describeTable(row, tableName);
      return label
        ? `${quote(label)} 표는 3개월 칸을 쓰지 않습니다`
        : REASON_LABEL.quarter_acc_not_supported;
    }
    case "not_used_sum_axis":
      return REASON_LABEL.not_used_sum_axis;
    // 축은 있는데 그 열이 안 열려 있는 것이라, 어느 축인지 같이 말해줘야
    // 사용자가 IXD 표에서 체크할 자리를 찾는다.
    case "uncovered_axis":
      return members
        ? `${REASON_LABEL.uncovered_axis} \u2014 ${members}`
        : REASON_LABEL.uncovered_axis;
    case "value_rejected":
      return `${quote(row.value)} 값의 형식이 IXD 계정과 맞지 않습니다`;
    case "currency_mismatch":
      return `${quote(row.value)} 값의 통화가 IXD 계정의 통화와 다릅니다`;
    case "entry_conversion_failed": {
      const period = [
        row.periodType
          ? (PERIOD_LABEL[row.periodType] ?? row.periodType)
          : null,
        row.accumulatedType
          ? (ACCUMULATED_LABEL[row.accumulatedType] ?? row.accumulatedType)
          : null,
      ]
        .filter(Boolean)
        .join(" ");
      return period
        ? `${quote(period)} 컨텍스트가 서식에 없습니다`
        : REASON_LABEL.entry_conversion_failed;
    }
  }
};

/** 태그의 기계용 id. 라벨과 달리 셀을 특정하는 데 쓸 수 있다 */
const tagId = (tag: IxdValueSyncTagLike): string =>
  typeof tag === "string" ? tag : tag.invectorKey || tag.id || "";

/**
 * 태그는 문자열로 올 수도, lineItem 같은 태그 객체로 올 수도 있다.
 * 템플릿 문자열에 그대로 넣으면 `[object Object]` 가 찍힌다.
 */
const describeTag = (tag: IxdValueSyncTagLike): string => {
  if (typeof tag === "string") return tag;
  return tag.labelKo || tag.invectorKey || tag.id || "";
};

const describeMembers = (
  members: readonly IxdValueSyncMember[],
): PlanDiagnosticMember[] =>
  members
    .map(({ axis, member, unresolvedReason }) => ({
      axis: describeTag(axis),
      member: describeTag(member),
      unresolvedReason: unresolvedReason ?? null,
    }))
    .filter(({ axis, member }) => axis || member);

/** 문장 안에 넣을 때의 한 줄 표기. `›` 가 쉼표보다 쌍을 또렷하게 만든다. */
const describeMember = ({ axis, member }: PlanDiagnosticMember): string =>
  axis && member ? `${axis} \u203A ${member}` : member || axis;

/** sourceAddress는 셀마다 유일하지만 null일 수 있어 인덱스를 섞어 키를 만든다. */
const rowKey = (prefix: string, address: string | null, index: number) =>
  `${prefix}:${address ?? "?"}:${index}`;

/** 한 role이 여러 그룹으로 갈렸을 때의 status 합산 — 하나라도 실패면 실패다. */
const rollUpStatus = (
  tables: readonly IxdValueSyncPlanTable[],
): IxdValueSyncPlanStatus => {
  if (tables.some(({ status }) => status === "failed")) return "failed";
  if (tables.some(({ status }) => status === "interpolated")) {
    return "interpolated";
  }
  return "success";
};

export const buildPlanTableDiagnostics = (
  tables: readonly IxdValueSyncPlanTable[] | undefined,
): PlanTableDiagnostics => {
  if (!tables || tables.length === 0) return EMPTY_DIAGNOSTICS;

  const rows: PlanDiagnosticRow[] = [];

  (tables ?? [])
    // 실패가 어느 그룹에서 왔는지 잃지 않는다 — 3개월 거부는 거부된 표를
    // 가리켜야 하는데 그 키가 그룹(ixdTableKey)에 있다.
    .flatMap((table) => table.failures.map((failure) => ({ failure, table })))
    .forEach(({ failure, table }, index) => {
      rows.push({
        key: rowKey("f", failure.sourceAddress, index),
        kind: "not-applied",
        label: failure.lineItem.labelKo || failure.lineItem.id,
        sourceAddress: failure.sourceAddress,
        members: describeMembers(failure.members),
        value: failure.value,
        reason: failure.reason,
        tableTag: failure.tableTag,
        periodType: failure.periodType,
        accumulatedType: failure.accumulatedType,
        ixdTableKey: table.ixdTableKey,
        lineItemId: tagId(failure.lineItem),
        begend: failure.begend,
        memberIds: failure.members.map((member) => tagId(member.member)),
      });
    });

  const count = (kind: PlanDiagnosticRow["kind"]) =>
    rows.filter((row) => row.kind === kind).length;

  return {
    status: tables && tables.length > 0 ? rollUpStatus(tables) : "success",
    notAppliedCount: count("not-applied"),
    // 이 표는 통째로 눈에 띄어야 한다 — 개별 셀 문제가 아니라 표를 못 찾은 것이다.
    isTableMissing: (tables ?? []).some(
      ({ ixdTableKey }) => ixdTableKey === null,
    ),
    rows,
  };
};
